import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import {
  FaMapMarkerAlt,
  FaPaperPlane,
  FaGlobeAmericas,
  FaChevronRight,
  FaCheckCircle,
  FaStar,
  FaChevronDown,
  FaTimesCircle,
  FaClock,
  FaBus,
  FaHotel,
  FaUtensils,
  FaShieldAlt,
  FaWater,
  FaUmbrellaBeach,
  FaShip,
  FaTree,
} from "react-icons/fa";

import "./GoaCoastal.css";

const GoaCoastal = () => {
  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({
      top: ref.current.offsetTop - 100,
      behavior: "smooth",
    });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  const itineraryData = [
    {
      day: 1,
      title: "Arrival → Leisure + Beach Experience",
      points: [
        "Arrival at Goa Airport / Railway Station and transfer to hotel.",
        "Check-in and relax after arrival.",
        "Visit nearby beaches: Baga Beach, Calangute Beach, and Candolim Beach.",
        "Enjoy beach walk and café experience in the evening.",
        "Explore local market and optional nightlife.",
        "Dinner and overnight stay.",
      ],
      icon: <FaUmbrellaBeach />,
    },

    {
      day: 2,
      title: "North Goa Sightseeing + Sunset",
      points: [
        "After breakfast proceed for North Goa sightseeing.",
        "Visit Fort Aguada and Sinquerim Beach.",
        "Explore Anjuna Beach and Vagator Beach.",
        "Visit Anjuna Flea Market (on operational days).",
        "Enjoy sunset from Chapora Fort with panoramic views.",
        "Evening free for beach shacks, cafés, and optional nightlife at Baga / Tito’s Lane.",
        "Dinner and overnight stay.",
      ],
      icon: <FaStar />,
    },

    {
      day: 3,
      title: "South Goa + Culture + Cruise",
      points: [
        "After breakfast proceed for South Goa sightseeing.",
        "Visit Basilica of Bom Jesus and Se Cathedral.",
        "Explore Dona Paula and Miramar Beach.",
        "Relax at Colva Beach and Benaulim Beach.",
        "Enjoy Mandovi River Cruise with music, dance, and cultural show.",
        "Dinner and overnight stay.",
      ],
      icon: <FaShip />,
    },

    {
      day: 4,
      title: "Experience Day (Offbeat + Nature)",
      points: [
        "After breakfast visit a Spice Plantation for a traditional Goan experience.",
        "Optional Dudhsagar Waterfalls excursion (seasonal activity).",
        "Explore Ashwem Beach and Morjim Beach.",
        "Enjoy café hopping and sunset relaxation in the evening.",
        "Dinner and overnight stay.",
      ],
      icon: <FaTree />,
    },

    {
      day: 5,
      title: "Departure",
      points: [
        "After breakfast check-out from hotel.",
        "Free time for local shopping and last beach visit.",
        "Transfer to Airport / Railway Station.",
        "Tour concludes with memorable Goa experiences.",
      ],
      icon: <FaCheckCircle />,
    },
  ];

  return (
    <div className="trip-container">

      {/* HEADER */}
      <motion.header
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="trip-header"
      >
        <div className="header-left">
          <h1 className="trip-title">
            Goa Coastal Escape
          </h1>

          <p className="trip-duration">
            4 Nights / 5 Days
          </p>
        </div>

        <div className="header-right">

          <div className="info-row">
            <FaMapMarkerAlt className="header-icon" />

            <div className="info-text">
              <span>Destinations:</span>
              <p>
                North Goa • South Goa • Beaches • Mandovi
              </p>
            </div>
          </div>

          <div className="info-row">
            <FaPaperPlane className="header-icon" />

            <div className="info-text">
              <span>Start & End:</span>
              <p>Goa</p>
            </div>
          </div>

          <div className="info-row type-tag">
            <FaGlobeAmericas className="header-icon" />
            <p>Beach • Culture • Leisure</p>
          </div>

        </div>
      </motion.header>

      {/* IMAGE GRID */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="image-grid-container"
      >

        <div className="grid-item large-vertical">
          <img
            src="https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?q=80&w=1200&auto=format&fit=crop"
            alt="Goa Beach"
          />
        </div>

        <div className="grid-item middle-box">
          <img
            src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1200&auto=format&fit=crop"
            alt="Beach Sunset"
          />
        </div>

        <div className="right-stack">

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1528127269322-539801943592?q=80&w=1200&auto=format&fit=crop"
              alt="Goa Cruise"
            />
          </div>

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1506744038136-46273834b3fb?q=80&w=1200&auto=format&fit=crop"
              alt="Goa Nature"
            />
          </div>

        </div>
      </motion.div>

      {/* STICKY NAV */}
      <nav className="sticky-nav">
        <button
          className="tab-link"
          onClick={() => scrollToSection(itineraryRef)}
        >
          Detailed Itinerary
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(highlightsRef)}
        >
          Highlights
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(inclusionsRef)}
        >
          Policy & Inclusions
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(aboutRef)}
        >
          About Trip
        </button>
      </nav>

      <div className="content-section-wrapper">

        {/* ITINERARY */}
        <section ref={itineraryRef} className="scroll-section">

          <h2 className="section-main-title">
            Tour Itinerary
          </h2>

          <div className="days-list">

            {itineraryData.map((item) => (
              <motion.div
                key={item.day}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.2 }}
                variants={fadeInUp}
                className="day-block"
              >

                <div className="day-header">
                  <span className="day-badge">
                    Day {item.day}
                  </span>

                  <h3>{item.title}</h3>
                </div>

                <div className="day-body">

                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}

                </div>
              </motion.div>
            ))}

          </div>
        </section>

        {/* HIGHLIGHTS */}
        <motion.section
          ref={highlightsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            Trip Highlights
          </h2>

          <div className="highlights-grid">

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Explore famous beaches like Baga, Calangute and Anjuna
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Visit historic Fort Aguada and Chapora Fort
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Discover Basilica of Bom Jesus and Se Cathedral
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Relax at peaceful beaches like Colva and Benaulim
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Enjoy Mandovi River cruise with music and entertainment
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Experience spice plantation and Goan culture
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Explore offbeat beaches like Ashwem and Morjim
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Optional adventure and nightlife experiences
              </p>
            </div>

          </div>
        </motion.section>

        {/* INCLUSIONS */}
        <motion.section
          ref={inclusionsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <div className="policy-box inclusion-box">

            <h4 className="p-title green">
              <FaCheckCircle />
              What's Included
            </h4>

            <ul className="p-list">

              <li>
                <FaHotel />
                Accommodation in hotels as per itinerary
              </li>

              <li>
                <FaUtensils />
                Daily breakfast and dinner
              </li>

              <li>
                <FaBus />
                All sightseeing as mentioned in the itinerary
              </li>

              <li>
                <FaBus />
                Transportation and transfers throughout the tour
              </li>

              <li>
                <FaCheckCircle />
                Driver allowance, fuel, toll and parking charges
              </li>

              <li>
                <FaShieldAlt />
                Tour coordination and assistance by Chandrama Holidays
              </li>

            </ul>
          </div>

          <div className="policy-box exclusion-box">

            <h4 className="p-title red">
              <FaTimesCircle />
              What's Excluded
            </h4>

            <ul className="p-list">

              <li>
                <FaTimesCircle />
                Airfare / train fare
              </li>

              <li>
                <FaWater />
                Water sports activities
              </li>

              <li>
                <FaTimesCircle />
                Dudhsagar waterfall entry / jeep charges
              </li>

              <li>
                <FaTimesCircle />
                Spice plantation entry charges
              </li>

              <li>
                <FaShip />
                Mandovi cruise tickets
              </li>

              <li>
                <FaTimesCircle />
                Entry tickets to monuments
              </li>

              <li>
                <FaTimesCircle />
                Personal expenses and shopping
              </li>

              <li>
                <FaUtensils />
                Lunch and additional meals
              </li>

              <li>
                <FaShieldAlt />
                Travel insurance
              </li>

              <li>
                <FaTimesCircle />
                Any additional sightseeing not mentioned
              </li>

            </ul>
          </div>
        </motion.section>

        {/* ABOUT */}
        <motion.section
          ref={aboutRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            About This Journey
          </h2>

          <p className="about-text">
            Experience the vibrant charm of Goa with a perfect blend of iconic
            beaches, cultural heritage, and offbeat experiences. This journey
            covers the lively coastline of North Goa, the serene beauty of
            South Goa, and unique local experiences like spice plantations and
            river cruises.
            <br /><br />
            Designed for a balanced mix of relaxation and exploration, this
            itinerary offers beach life, heritage sites, scenic sunsets, and
            optional adventure activities. Travelers can enjoy beach cafés,
            cultural attractions, tropical sunsets, and the famous Goan
            hospitality throughout the tour.
          </p>

        </motion.section>

        {/* TERMS */}
        <div className="accordion-item">
          <Link
            to="/terms-conditions"
            className="accordion-header"
            style={{
              textDecoration: "none",
              color: "inherit",
            }}
          >

            <span>Terms & Conditions</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: "rotate(-90deg)" }}
            />
          </Link>
        </div>

        {/* PRIVACY */}
        <div className="accordion-item">
          <Link
            to="/privacy-policy"
            className="accordion-header"
            style={{
              textDecoration: "none",
              color: "inherit",
            }}
          >

            <span>Privacy Policy</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: "rotate(-90deg)" }}
            />
          </Link>
        </div>

        {/* IMPORTANT NOTES */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section travel-info-grid"
        >

          <div
            className="info-card note-card"
            style={{ width: "100%" }}
          >

            <h4 className="info-card-title">
              <FaPaperPlane />
              Important Notes
            </h4>

            <p>
              • Mandovi River cruise is subject to timing and availability.
              <br /><br />
              • Dudhsagar Waterfalls visit is seasonal and depends on weather conditions.
              <br /><br />
              • Some beaches and markets may vary based on local conditions and crowd.
            </p>

          </div>

        </motion.section>

      </div>
    </div>
  );
};

export default GoaCoastal;